'use client';

import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginFormSchema } from '../../lib/login-schema';
import { loginAdmin } from '@/app/lib/auth';
import { useState, useEffect } from 'react';
import { redirectIfLoggedIn } from '../../lib/sessionCheck';
import { useFlash } from '../../lib/FlashMessageProvider';
import { FiEye, FiEyeOff } from "react-icons/fi";
import { z } from 'zod';

export default function LoginPage() {
  const router = useRouter();
  const { addMessage } = useFlash();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    redirectIfLoggedIn(router);
  }, [router]);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(LoginFormSchema),
    mode: 'onChange',
  });

  const getFlashType = (status) => {
    if (status >= 200 && status < 300) return 'success';
    if (status === 400) return 'warning';
    if (status === 401 || status === 403) return 'error';
    if (status === 404) return 'info';
    if (status >= 500) return 'error';
    return 'info';
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    const result = await loginAdmin({ email: data.email, password: data.password });

    if (result.success) {
      const { name, organisation, role } = result.data;

      sessionStorage.setItem('employee_name', name);
      sessionStorage.setItem('organisation', organisation);
      sessionStorage.setItem('role', role);

      console.log('✅ Login successful — Role:', role, '| Org:', organisation);
      addMessage('success', 'Login successful!');

      setTimeout(() => {
        router.push('/auth/select-organisation');
      }, 100);

    } else {
      addMessage(getFlashType(result.status), result.message || 'Invalid credentials.');
      console.log('❌ Login failed:', result);
    }

    setIsSubmitting(false);
  };

  return (
    <div className="w-100 container-login">
      <style>{`
        @media (max-width: 575.98px) {
          .container-login {
            padding: 1rem !important;
          }
          .custom-input {
            font-size: 14px;
            padding: 8px 10px;
          }
          .password-wrapper .eye-icon {
            top: 50%;
            transform: translateY(-50%);
          }
          .btn-submit,
          .btn-submit-org-id {
            font-size: 15px;
            padding: 10px;
          }
          .auth-text {
            font-size: 13px;
          }
          label {
            font-size: 13px;
          }
        }

        @media (max-width: 374px) {
          .custom-input {
            font-size: 13px;
          }
        }
      `}</style>
      <h3 className="mb-1 font-bold ">Welcome back</h3>
      <p className="text-muted mb-4 fs-7 font-semibold">We're glad to see you again. Ready to continue managing your payroll?</p>

      <form onSubmit={handleSubmit(onSubmit)} className='login-form'>
        <div className="form-group">
          <label>Admin Email</label>
          <input
            type="text"
            className={`custom-input ${errors.email ? 'error' : ''}`}
            placeholder="Enter your email"
            {...register('email')}
          />
          {errors.email && <span className="error-text">{errors.email.message}</span>}
        </div>

        <div className="form-group mt-3">
          <label>Password</label>
          <div className="password-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              className={`custom-input ${errors.password ? 'error' : ''}`}
              placeholder="Enter your password"
              {...register('password')}
            />
            <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </span>
          </div>
          {errors.password && <span className="error-text">{errors.password.message}</span>}
        </div>

        <div className="row mt-2 mb-3">
          <div className="col">
            <input type="checkbox" /> Remember me
          </div>
          <div className="col text-end">
            <Link href="/auth/forget-password" className="forgot-text">Forgot password?</Link>
          </div>
        </div>

        <button type="submit" className="btn btn-submit w-100 mb-2" disabled={isSubmitting}>
          {isSubmitting ? 'Signing in...' : 'Sign in'}
        </button>

        <button type="button" className="btn btn-submit-org-id w-100 mt-2">
          Sign in with Organisation ID
        </button>

        <p className="auth-text mt-3">
          Don't have an account? <Link href="/auth/register">Signup now</Link>
        </p>

        <p className="auth-text">
          Are you an employee? <Link href="/auth/employee-login">Sign in</Link>
        </p>
      </form>
    </div>
  );
}